import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import { CognitoUser } from "amazon-cognito-identity-js";
import UserPool from '../../../assets/constants/user-pool';
import SnackBar from '../snackbar/snackbar';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'white',
    padding: '20px'
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  textField: {
    width: '100%',
    marginTop: '10px'
  },
}));

const ForgotPassword = () => {
  const [snackBarStatus, setSnackBarStatus] = useState({ open: false, message: '' });
  const [stage, setStage] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [values, setValues] = useState({
    showPasswordType: false,
    showConfirmPasswordType: false
  });

  useEffect(() => {
    document.getElementsByTagName("body")[0].classList.add("normal-layout-container");
    document.getElementsByTagName("body")[0].classList.remove("awe-layout-container");
  }, []);

  const handleClickShowPassword = () => {
    setValues({ ...values, showPasswordType: !values.showPasswordType });
  };

  const handleClickShowConfirmPassword = () => {
    setValues({ ...values, showConfirmPasswordType: !values.showConfirmPasswordType });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const getUser = () => {
    return new CognitoUser({
      Username: email.toLowerCase().trim(),
      Pool: UserPool
    });
  };

  const sendCode = (event) => {
    event.preventDefault();
    
    if (!email) {
      setSnackBarStatus({ open: true, message: 'Email is required!' });
      return;
    }
    
    const user = getUser();
    
    user.forgotPassword({
      onSuccess: data => {
        setSnackBarStatus({ open: true, message: 'Verification code sent successfully!' });
        setStage(2);
      },
      onFailure: err => {
        console.error("Error sending verification code:", err);
        setSnackBarStatus({ 
          open: true, 
          message: `Error: ${err.message || 'Failed to send verification code'}` 
        });
      },
      inputVerificationCode: data => {
        setSnackBarStatus({ open: true, message: 'Verification code sent successfully!' });
        setStage(2);
      }
    });
  };

  const resetPassword = event => {
    event.preventDefault();

    if (!code) {
      setSnackBarStatus({ open: true, message: 'Verification code is required!' });
      return;
    }

    if (!password || !confirmPassword) {
      setSnackBarStatus({ open: true, message: 'Password fields cannot be empty!' });
      return;
    }

    if (password !== confirmPassword) {
      setSnackBarStatus({ open: true, message: 'Passwords do not match!' });
      return;
    }
    
    // Ensure code is properly formatted as a string
    const verificationCode = code.toString().trim();
    
    getUser().confirmPassword(verificationCode, password, {
      onSuccess: data => {
        setSnackBarStatus({ open: true, message: 'Password changed successfully!' });
        setStage(1);
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setCode("");
      },
      onFailure: err => {
        console.error("Password reset error:", err);
        setSnackBarStatus({ 
          open: true, 
          message: `Error: ${err.message || 'Failed to change password'} (${err.code || 'unknown error'})` 
        });
      }
    });
  };

  const handleCloseSnackbar = () => {
    setSnackBarStatus({ ...snackBarStatus, open: false });
  };

  const classes = useStyles();
  return (
    <div>
      <SnackBar 
        open={snackBarStatus.open} 
        message={snackBarStatus.message} 
        onClose={handleCloseSnackbar}
      />
      <form className={classes.form} noValidate autoComplete="off">
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          value={email}
          onChange={event => setEmail(event.target.value)}
          autoFocus
        />
        <Button 
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={sendCode}
          disabled={!email}
        >
          Send Verification Code
        </Button>
        {stage === 2 && (
          <>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="code"
              label="Verification Code"
              name="code"
              value={code}
              onChange={event => setCode(event.target.value)}
              autoFocus
            />
            <FormControl className={clsx(classes.textField)} variant="outlined">
              <InputLabel htmlFor="password">Password *</InputLabel>
              <OutlinedInput
                fullWidth
                required
                id="password"
                type={values.showPasswordType ? 'text' : 'password'}
                value={password}
                onChange={event => setPassword(event.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {values.showPasswordType ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={100}
              />
            </FormControl>
            <FormControl className={clsx(classes.textField)} variant="outlined">
              <InputLabel htmlFor="confirm-password">Confirm Password *</InputLabel>
              <OutlinedInput
                fullWidth
                required
                id="confirm-password"
                type={values.showConfirmPasswordType ? 'text' : 'password'}
                value={confirmPassword}
                onChange={event => setConfirmPassword(event.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {values.showConfirmPasswordType ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                labelWidth={150}
              />
            </FormControl>
            <Button 
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={resetPassword}
              disabled={!code || !password || !confirmPassword}
            >
              Change Password
            </Button>
          </>
        )}
      </form>
    </div>
  );
};

export default ForgotPassword;